import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertCircle, RefreshCw, Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reportError } from "@/lib/diagnostics";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    void reportError("ErrorBoundary", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8 min-h-[200px] text-center">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <h2 className="text-lg font-semibold">
            {this.props.fallbackTitle ?? "Une erreur est survenue"}
          </h2>
          <p className="text-sm text-muted-foreground max-w-md">
            {this.state.error?.message || "Erreur inattendue"}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Réessayer
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { window.location.href = "/debug/cache"; }}
            >
              <Bug className="h-4 w-4 mr-2" /> Diagnostic
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
